import * as ents from '@attain-apps/entities'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type { Feature } from '../feature'
import type * as models from '../models'

/**
 * Shows one sticky warning toast while GraphQL requests are slow,
 * then briefly warns when a slow request still fails.
 */
export const graphqlRetry = store.effects.create((feature: Feature) => {
  // Only requests that emitted `slow` are tracked here; retries by themselves
  // should not create, dismiss, or follow up on any toast.
  const slowRequestIds = new Set<string>()
  let toastId: models.Toast.Id | undefined

  return graphql.retryRequest.events$.pipe(
    rxjs.mergeMap(event => {
      switch (event.type) {
        case 'slow': {
          // `slow` means the user has already waited long enough to deserve
          // feedback, so this is the only event type that can show the toast.
          slowRequestIds.add(event.requestId)
          if (toastId) {
            return rxjs.EMPTY
          }

          const action = feature.actions.show
            .from('Toaster.GraphqlRetry.RequestDelayed')
            .create({
              toast: {
                description:
                  event.message ?? 'Still trying to reach the server.',
                durationMs: null,
                name: 'Connection is slow',
                style: ents.core.display.Style.Warning,
              },
            })
          toastId = action.data.toasts[0]?.id
          return rxjs.of(action)
        }
        case 'retrying': {
          // Retrying can happen quickly in the background; keep that invisible.
          return rxjs.EMPTY
        }
        case 'failed': {
          // If the request only retried and never became slow, do not show a
          // failure follow-up toast either.
          if (!slowRequestIds.delete(event.requestId)) {
            return rxjs.EMPTY
          }

          const failedAction = feature.actions.show
            .from('Toaster.GraphqlRetry.RequestFailed')
            .create({
              toast: {
                description:
                  'If something looks incomplete, refreshing the page may help.',
                durationMs: 30_000,
                name: 'Some information did not load',
                style: ents.core.display.Style.Info,
              },
            })

          if (slowRequestIds.size > 0 || !toastId) {
            return rxjs.of(failedAction)
          }

          const dismissToastId = toastId
          toastId = undefined
          return rxjs.from([
            feature.actions.dismiss
              .from('Toaster.GraphqlRetry.RequestSettled')
              .create({ id: dismissToastId }),
            failedAction,
          ])
        }
        case 'recovered': {
          // Ignore recovery for retry-only requests, since no slow toast exists
          // for them and there is nothing to dismiss.
          if (!slowRequestIds.delete(event.requestId)) {
            return rxjs.EMPTY
          }

          if (slowRequestIds.size > 0 || !toastId) {
            return rxjs.EMPTY
          }

          const dismissToastId = toastId
          toastId = undefined
          return rxjs.of(
            feature.actions.dismiss
              .from('Toaster.GraphqlRetry.RequestSettled')
              .create({ id: dismissToastId }),
          )
        }
      }
    }),
  )
})
